import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import buyerModule from '../api/modules/buyer.module';
import { useAuth } from '../hooks/useAuth';
import { invalidateFavourites } from './favouriteQueries';
import { readGuestFavourites, removeGuestFavourites } from './guestFavourites';

/**
 * Moves a guest's favourites into their account.
 *
 * THE TRIGGER IS STATE, NOT AN EVENT: "authenticated AND the guest list is not
 * empty", never "login just happened". `checkAuth()` restores a session on
 * mount without passing through `login()` (hooks/useAuth.js), so a buyer who
 * favourites, refreshes, then signs in would never merge if this hung off the
 * login call. One condition covers login, signup and a restored session.
 *
 * WHAT IT COSTS EVERYONE ELSE: one localStorage read. The overwhelming majority
 * of sign-ins have no guest list, and this must not put a request on their path.
 *
 * EVERY ID IS ATTEMPTED, AND ONLY THE IDS THAT SETTLED ARE DROPPED.
 * -----------------------------------------------------------------
 * This used to be `Promise.all` over batches with a throw inside the worker.
 * One id the catalog no longer resolves (`favourites/add/` answers 400) made
 * the batch reject, the batch loop `break` out, and every id after it was
 * never POSTed — not on that attempt and not on any later one, because the
 * retry on the next page load hit the same id in the same position and failed
 * identically. The buyer's cards never reached their account and, being signed
 * in, they could no longer see the guest list either. One dead id ate the list.
 *
 * So: attempt all of them, settle each one independently, and take out of
 * localStorage only what is now genuinely accounted for.
 *
 * WHAT COUNTS AS ACCOUNTED FOR
 * ----------------------------
 * - saved      -> the server has it. Drop it. (This is the original invariant:
 *                 never drop an id the server does not have.)
 * - rejected   -> the server answered 400/404, which for this endpoint means
 *                 "card_lookup_id does not resolve to a Card". No number of
 *                 retries changes that, and a card the server will never accept
 *                 must not wedge the list forever: it would re-POST on every
 *                 page load for the life of the browser profile, and the buyer
 *                 cannot see or clear it because they are signed in. Drop it.
 *                 "Never" is a narrow claim -- see `isPermanentRejection`, which
 *                 requires the server to have said the card does not exist, not
 *                 merely to have answered 4xx.
 * - retry      -> anything else: offline, 5xx, 429, or a 401 from a token that
 *                 expired mid-merge. All of those are "later, not never".
 *                 KEEP it, and try again on the buyer's next page load.
 *
 * 401, 403, 429, 5xx and a generic 400 are all deliberately "later, not never".
 * Those are the failures most likely to hit the whole list at once, and dropping
 * a list because a token expired or a database blinked is exactly the data loss
 * this hook exists to avoid.
 *
 * ORDER OF OPERATIONS: POST everything, refetch the server list, and only then
 * touch localStorage. `favourites/add/` is `get_or_create` server-side, so a
 * duplicate is free and a retry after a half-finished merge is safe.
 */
const CONCURRENCY = 4;

/* THE ONLY VERDICT THAT MEANS "THIS CARD WILL NEVER RESOLVE".
   Status alone is not enough, and getting this wrong deletes saved cards.
   `add_to_favourites` returns 400 from THREE places (apps/api/apps/cards/views.py):

     "card_lookup_id is required"   a malformed request
     "Card not found"               the id resolves to no Card  <- the real verdict
     "Error adding to favourites"   its blanket `except Exception` -- a DB blip,
                                    a bug, anything at all

   Dropping on bare 400 therefore threw a buyer's card away because the server
   hiccuped during the one merge they ever run. And the endpoint has no 404
   branch at all: a 404 means the ROUTE is wrong, which hits every id at once,
   so reading it as "none of these cards exist" would wipe a whole list on a bad
   deploy.

   Matching the message couples this to server copy, which is a real cost. It is
   paid deliberately, and pinned from the other end: `test_add_to_favourites`
   asserts this exact string, so renaming it server-side breaks a test instead of
   quietly turning card losses back on. */
const CARD_NOT_FOUND = 'Card not found';

function isPermanentRejection(result) {
  return result?.status === 400 && result?.error === CARD_NOT_FOUND;
}

/** Like `Promise.all` in batches, except the worker is required not to throw. */
async function mapInBatches(items, size, worker) {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await Promise.all(items.slice(i, i + size).map(worker));
    out.push(...batch);
  }
  return out;
}

/* Resolves, never rejects -- that is the whole point. `buyerModule.saveCard`
   already swallows its own errors into `{ success: false, status, error }`, but
   the try/catch is kept so a future change there cannot silently restore the
   abort-the-whole-list behaviour. */
async function attemptSave(cardId) {
  try {
    const result = await buyerModule.saveCard(cardId);
    if (result?.success) return { cardId, outcome: 'saved' };
    if (isPermanentRejection(result)) {
      return { cardId, outcome: 'rejected', error: result?.error };
    }
    return { cardId, outcome: 'retry', error: result?.error };
  } catch (error) {
    return { cardId, outcome: 'retry', error };
  }
}

export function useGuestFavouriteMerge() {
  const { isAuthenticated, initialized } = useAuth();
  const queryClient = useQueryClient();
  /* One merge per page load. Without this, any re-render between the POSTs and
     the localStorage write would start a second pass over the same ids. */
  const running = useRef(false);

  useEffect(() => {
    /* `initialized` matters: before auth settles, `isAuthenticated` is false for
       everyone, and acting on that would treat a signed-in buyer as a guest. */
    if (!initialized || !isAuthenticated || running.current) return;

    const ids = readGuestFavourites();
    if (ids.length === 0) return;

    running.current = true;

    (async () => {
      try {
        const outcomes = await mapInBatches(ids, CONCURRENCY, attemptSave);

        const saved = outcomes.filter((o) => o.outcome === 'saved');
        const rejected = outcomes.filter((o) => o.outcome === 'rejected');
        const retry = outcomes.filter((o) => o.outcome === 'retry');

        /* Refetch before the local list is touched, so the account list is
           already showing the cards by the time they leave the device. */
        if (saved.length > 0) await invalidateFavourites(queryClient);

        const settled = [...saved, ...rejected].map((o) => o.cardId);
        if (settled.length > 0) removeGuestFavourites(settled);

        if (rejected.length > 0) {
          console.error(
            'Dropped guest favourites the catalog no longer resolves:',
            rejected.map((o) => o.cardId),
          );
        }
        if (retry.length > 0) {
          /* Deliberately silent to the buyer: they did not ask for a migration
             and an error toast about one they never requested is noise. These
             ids are still on the device. Resetting `running.current` does not
             retrigger this effect mid-session (its deps do not change), so the
             retry happens on the buyer's next page load, when the component
             mounts fresh and the effect runs again from the top. */
          console.error(
            'Failed to merge guest favourites:',
            retry.map((o) => o.cardId),
          );
          running.current = false;
        }
      } catch (error) {
        /* Only reachable if the refetch or the storage write throws. Nothing
           has been removed from the device at this point. */
        console.error('Failed to merge guest favourites:', error);
        running.current = false;
      }
    })();
  }, [initialized, isAuthenticated, queryClient]);
}

/** Render-null host, so the effect can be mounted once at the app root. */
export function GuestFavouriteMerge() {
  useGuestFavouriteMerge();
  return null;
}

export default useGuestFavouriteMerge;
